/**
 * Staying in step with the server.
 *
 * HOW IT WORKS
 *   1. Read the replica out of IndexedDB and render it. This is local, so the
 *      file tree is on screen before any request has left the machine.
 *   2. Open ONE event stream. The server pushes every change, every comment
 *      and every presence update down it, and sends a heartbeat so a dead
 *      connection is noticed rather than silently trusted.
 *   3. Apply our OWN writes the moment their request returns, rather than
 *      waiting for the stream to echo them back. The echo then merges to
 *      nothing, because merging is by id.
 *
 * WHY A STREAM RATHER THAN POLLING
 *   The first version long-polled: every client re-issued a request every 25
 *   seconds whether or not anything had happened, and every re-poll was a
 *   chance to lose its place. A stream costs one connection for as long as the
 *   tab is open, pushes in tens of milliseconds, and — because being connected
 *   IS being present — makes presence a property of the connection rather than
 *   something that could disagree with reality.
 *
 * THE BUG THIS DESIGN EXISTS TO PREVENT
 *   A cursor is a position in one particular log. When the server's log was
 *   rebuilt, clients kept asking for changes after a sequence number the new
 *   log would never reach, received nothing, and were stranded permanently —
 *   with no error anywhere, because "no new changes" looks exactly like "no
 *   new changes". Now the server stamps its log with an EPOCH, we store it
 *   beside the cursor, and either side noticing a mismatch forces a rebuild.
 *   A cursor ahead of the server's is treated the same way: it is impossible
 *   in a healthy system, so it means the same thing.
 */
import { api, ApiError, onMutation, type Delta, type Hello } from './api'
import { loadSnapshot, saveSnapshot, clearSnapshot } from './persist'
import { useWorkspace } from './workspace'
import type { Presence } from '@lattice/core'

/** How long to wait before re-opening a stream, growing to a ceiling. */
const BACKOFF_MS = [500, 1_000, 2_000, 5_000, 10_000, 20_000]

/**
 * A stream that has produced nothing at all for this long is assumed dead.
 * The server beats every 15 s, so three missed beats is a confident verdict
 * without being trigger-happy about a slow network.
 */
const SILENCE_LIMIT_MS = 50_000

let source: EventSource | undefined
let wanted = false
let attempt = 0
let reopenTimer: ReturnType<typeof setTimeout> | undefined
let watchdog: ReturnType<typeof setInterval> | undefined
let lastFrameAt = 0
/** Guards against two resyncs racing when several signals arrive at once. */
let resyncing = false

/* ------------------------------------------------------------------ */
/* Boot                                                                */
/* ------------------------------------------------------------------ */

/**
 * Bring the replica up, rendering from cache first.
 * boot : -> Promise<void>
 */
export async function boot(): Promise<void> {
  const cached = await loadSnapshot()
  if (cached && cached.changes.length > 0) {
    useWorkspace.getState().hydrate({
      changes: cached.changes,
      comments: cached.comments,
      locks: cached.locks ?? [],
      projects: cached.projects,
      cursor: cached.cursor,
      ...(cached.epoch ? { epoch: cached.epoch } : {}),
      fromCache: true,
    })
  }

  try {
    useWorkspace.getState().setSyncState('syncing')
    await refresh()
    wanted = true
    connect()
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      useWorkspace.getState().reset()
      return
    }
    useWorkspace.getState().setSyncState('offline')
    if (!cached || cached.changes.length === 0) {
      useWorkspace.getState().setStatus('error', (e as Error).message)
    }
    scheduleReopen()
  }
}

/**
 * Fetch a complete, authoritative snapshot and replace the replica with it.
 *
 * This is the recovery path for every "we cannot trust what we hold" case:
 * no cache, a different epoch, a cursor from a log that no longer exists.
 * It is deliberately a REPLACEMENT rather than a merge — merging into state
 * we have already decided is untrustworthy is how a corrupt replica survives.
 * refresh : -> Promise<void>
 */
async function refresh(): Promise<void> {
  const full = await api.bootstrap()
  useWorkspace.getState().hydrate({
    user: full.user,
    roster: full.roster,
    projects: full.projects,
    changes: full.changes,
    comments: full.comments,
    locks: full.locks,
    cursor: full.cursor,
    epoch: full.epoch,
    presence: full.presence,
    readMark: full.readMark,
  })
  useWorkspace.getState().setSyncState('idle')
  persist()
}

/* ------------------------------------------------------------------ */
/* The stream                                                          */
/* ------------------------------------------------------------------ */

/** Open the event stream, replacing any existing one. connect : -> void */
export function connect(): void {
  if (!wanted) return
  disconnect()

  const { cursor, epoch } = useWorkspace.getState()
  const q = new URLSearchParams({ cursor: String(cursor) })
  if (epoch) q.set('epoch', epoch)

  const stream = new EventSource(`/api/stream?${q}`)
  source = stream
  lastFrameAt = Date.now()

  const seen = (): void => { lastFrameAt = Date.now() }

  stream.addEventListener('hello', event => {
    seen()
    const hello = parse<Hello>(event)
    if (!hello) return
    useWorkspace.getState().setConnection(hello.connection)
    useWorkspace.getState().setSyncState('idle')
    attempt = 0
    if (hello.resync) {
      void resync()
      return
    }
    // The epoch is authoritative; record it so a later reconnect sends it.
    if (hello.epoch !== useWorkspace.getState().epoch) {
      useWorkspace.getState().hydrate({
        changes: useWorkspace.getState().changes,
        comments: useWorkspace.getState().comments,
        cursor: useWorkspace.getState().cursor,
        epoch: hello.epoch,
      })
    }
  })

  stream.addEventListener('delta', event => {
    seen()
    const delta = parse<Delta>(event)
    if (!delta) return
    // A delta from a different log must never be merged in.
    const epoch = useWorkspace.getState().epoch
    if (delta.epoch && epoch && delta.epoch !== epoch) { void resync(); return }
    if (delta.changes.length === 0 && delta.comments.length === 0 && !delta.locks?.length) return
    useWorkspace.getState().applyDelta(delta)
    persist()
    notify(delta.cursor)
  })

  stream.addEventListener('presence', event => {
    seen()
    const payload = parse<{ presence: Presence[] }>(event)
    if (payload) useWorkspace.getState().setPresence(payload.presence)
  })

  stream.addEventListener('ping', seen)
  stream.onopen = () => { seen(); attempt = 0; useWorkspace.getState().setSyncState('idle') }

  // EventSource retries on its own, but on its own schedule and without our
  // cursor. We own reconnection so the URL always carries a current position.
  stream.onerror = () => {
    if (!wanted) return
    useWorkspace.getState().setSyncState('offline')
    scheduleReopen()
  }

  clearInterval(watchdog)
  watchdog = setInterval(() => {
    if (!wanted || !source) return
    if (Date.now() - lastFrameAt <= SILENCE_LIMIT_MS) return
    // Silent for longer than the heartbeat allows: the socket is gone even
    // though no error fired, which a sleeping laptop does routinely.
    useWorkspace.getState().setSyncState('offline')
    scheduleReopen()
  }, 10_000)
}

/** Read a frame's JSON, ignoring anything malformed rather than throwing. */
function parse<T>(event: MessageEvent): T | undefined {
  try { return JSON.parse(event.data) as T } catch { return undefined }
}

/** Close the stream, if any. disconnect : -> void */
function disconnect(): void {
  clearInterval(watchdog)
  watchdog = undefined
  source?.close()
  source = undefined
  useWorkspace.getState().setConnection(undefined)
}

function scheduleReopen(): void {
  disconnect()
  if (!wanted) return
  clearTimeout(reopenTimer)
  const wait = BACKOFF_MS[Math.min(attempt++, BACKOFF_MS.length - 1)]
  reopenTimer = setTimeout(() => { if (wanted) connect() }, wait)
}

/**
 * Throw the replica away and rebuild it, then re-open the stream at the new
 * position. Serialised, because several signals can demand it at once.
 * resync : -> Promise<void>
 */
async function resync(): Promise<void> {
  if (resyncing) return
  resyncing = true
  disconnect()
  try {
    await clearSnapshot()
    await refresh()
    attempt = 0
    connect()
  } catch {
    useWorkspace.getState().setSyncState('offline')
    scheduleReopen()
  } finally {
    resyncing = false
  }
}

/** Stop syncing: sign-out, or the window going away. */
export function stopSync(): void {
  wanted = false
  clearTimeout(reopenTimer)
  disconnect()
}

/** Start syncing if it is not already running. */
export function startSync(): void {
  if (wanted) return
  wanted = true
  connect()
}

/* ------------------------------------------------------------------ */
/* Our own writes                                                      */
/* ------------------------------------------------------------------ */

/**
 * Apply what our own writes returned, the moment they return.
 *
 * Subscribing to the api module rather than calling this from each dialog
 * means no call site can forget, and it is why a rename appears under your own
 * cursor instantly instead of a round trip later. The stream delivers the same
 * rows shortly afterwards; because merging is by id, that second arrival is a
 * no-op.
 *
 * The cursor is deliberately NOT advanced past these rows. The server's
 * sequence may already have gone further — someone else writing at the same
 * moment — and claiming their sequence number would skip their row.
 */
onMutation(({ changes = [], comments = [], locks = [] }) => {
  if (changes.length === 0 && comments.length === 0 && locks.length === 0) return
  useWorkspace.getState().applyDelta({ changes, comments, locks, cursor: 0 })
  persist()
})

/* ------------------------------------------------------------------ */
/* Where we are looking                                                */
/* ------------------------------------------------------------------ */

let lastReported = ''

/**
 * Tell the server which file this person has open, so colleagues can see it.
 *
 * Sent only when it actually changes, and never blocking anything: presence is
 * a courtesy, and a failed report should be invisible.
 * reportViewing : String|undefined String|undefined -> void
 */
export function reportViewing(projectId?: string, path?: string): void {
  const connection = useWorkspace.getState().connection
  if (!connection) return
  const key = `${connection}|${projectId ?? ''}|${path ?? ''}`
  if (key === lastReported) return
  lastReported = key
  void api.presence(connection, { ...(projectId ? { projectId } : {}), ...(path ? { path } : {}) })
    .catch(() => undefined)
}

/* ------------------------------------------------------------------ */
/* Persistence                                                         */
/* ------------------------------------------------------------------ */

let persistTimer: ReturnType<typeof setTimeout> | undefined

/** Write the replica to IndexedDB, coalescing bursts. persist : -> void */
export function persist(): void {
  clearTimeout(persistTimer)
  persistTimer = setTimeout(() => {
    const { changes, comments, locks, projects, cursor, epoch } = useWorkspace.getState()
    void saveSnapshot({ changes, comments, locks, projects, cursor, ...(epoch ? { epoch } : {}) })
  }, 400)
}

/** Forget the local replica. Called on sign-out so nothing leaks between people. */
export async function forgetReplica(): Promise<void> {
  stopSync()
  await clearSnapshot()
  useWorkspace.getState().reset()
}

/* ------------------------------------------------------------------ */
/* Notifications                                                       */
/* ------------------------------------------------------------------ */

type Listener = (cursor: number) => void
const listeners = new Set<Listener>()

/** Be told when new rows arrive. onChange : Listener -> (-> void) */
export function onChange(listener: Listener): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

function notify(cursor: number): void {
  for (const listener of listeners) listener(cursor)
}
